<script setup lang="ts">
/**
 * QuickEnquiryForm
 *
 * A compact lead capture form used in hero sections and CTA banners.
 * Submits to the leads API with reCAPTCHA verification.
 * Uses shadcn-vue form components for consistent styling and accessibility.
 */

import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { isSupportIntent } from '~/composables/useAnalytics';

const { executeRecaptcha } = useRecaptcha();
const {
  trackLeadFormStart, trackLeadSubmit,
  trackSupportFormStart, trackSupportSubmit,
} = useAnalytics();
const attribution = useAttribution();
const isSupport = computed(() => isSupportIntent(props.service));

interface Props {
  service?: string;
  variant?: "hero" | "inline" | "modal";
  title?: string;
  submitText?: string;
  defaultMessage?: string;
  showCompany?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  service: "",
  variant: "hero",
  title: "Get a Free Quote",
  submitText: "Send Enquiry",
  defaultMessage: "",
  showCompany: true,
});

const emit = defineEmits<{
  success: [];
}>();

// Australian phone: digits, spaces, +, -, (, ), . only
const auPhoneRegex = /^(?=.*\d)[\d\s+\-().]+$/;

// Form validation schema
const formSchema = z.object({
  name: z.string().min(1, "Name is required").min(2, "Name must be at least 2 characters"),
  email: z.string().min(1, "Email is required").email("Please enter a valid email"),
  phone: z.string().min(1, "Phone is required").min(8, "Please enter a valid phone number").max(20).regex(auPhoneRegex, "Please enter a valid phone number"),
  postcode: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || /^\d{4}$/.test(v), "Please enter a valid 4-digit Australian postcode"),
  company: z.string().optional(),
  message: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;
type RequiredField = "name" | "email" | "phone";

// Honeypot field — hidden from users, filled by bots
const honeypot = ref("");

// Form state
const form = reactive<FormData>({
  name: "",
  email: "",
  phone: "",
  postcode: "",
  company: "",
  message: props.defaultMessage,
});

const errors = ref<Partial<Record<keyof FormData, string>>>({});
const touched = ref<Partial<Record<RequiredField, boolean>>>({});
const hasAttemptedSubmit = ref(false);
const isSubmitting = ref(false);
const isSuccess = ref(false);
const submitError = ref("");
const hasTrackedFormStart = ref(false);

// Track form interaction start (only once per form instance)
function handleFormInteraction() {
  if (!hasTrackedFormStart.value) {
    const service = props.service || undefined;
    if (isSupport.value) {
      trackSupportFormStart('quick_enquiry', service);
    } else {
      trackLeadFormStart('quick_enquiry', service);
    }
    hasTrackedFormStart.value = true;
  }
}

// Check if an error should be shown (field touched or form submitted)
function shouldShowError(field: RequiredField): boolean {
  return !!(errors.value[field] && (touched.value[field] || hasAttemptedSubmit.value));
}

// Mark field as touched and validate
function handleBlur(field: RequiredField) {
  handleFormInteraction();
  touched.value[field] = true;
  validateField(field);
}

// Validate a single field
function validateField(field: keyof FormData) {
  const fieldSchema = formSchema.shape[field];
  const result = fieldSchema.safeParse(form[field]);

  if (!result.success) {
    errors.value[field] = result.error.issues[0]?.message;
  } else {
    errors.value[field] = undefined;
  }
}

// Validate all fields
function validateForm(): boolean {
  // Clear previous errors
  errors.value = {};

  const result = formSchema.safeParse(form);

  if (!result.success) {
    result.error.issues.forEach((err) => {
      const field = err.path[0] as keyof FormData;
      // Only set the first error for each field
      if (!errors.value[field]) {
        errors.value[field] = err.message;
      }
    });
    return false;
  }

  return true;
}

// Submit form
async function handleSubmit() {
  hasAttemptedSubmit.value = true;

  if (!validateForm()) return;

  isSubmitting.value = true;
  submitError.value = "";

  try {
    // Get reCAPTCHA token
    const recaptchaToken = await executeRecaptcha("quick_enquiry");

    const response = await $fetch("/api/leads/quick-enquiry", {
      method: "POST",
      body: {
        ...form,
        ...attribution,
        service: props.service,
        honeypot: honeypot.value,
        recaptchaToken,
      },
    });

    // Check for success response
    if (response && typeof response === "object" && "success" in response) {
      if (response.success) {
        isSuccess.value = true;
        if (isSupport.value) {
          trackSupportSubmit('quick_enquiry', props.service || undefined);
        } else {
          await trackLeadSubmit('quick_enquiry', props.service || undefined, {
            email: form.email,
            phone: form.phone,
          });
        }
        emit("success");

        // Reset form after success
        Object.assign(form, {
          name: "",
          email: "",
          phone: "",
          postcode: "",
          company: "",
          message: "",
        });
        return;
      }
    }

    // If we got here without success, still mark as success (old API format)
    isSuccess.value = true;
    if (isSupport.value) {
      trackSupportSubmit('quick_enquiry', props.service || undefined);
    } else {
      await trackLeadSubmit('quick_enquiry', props.service || undefined, {
        email: form.email,
        phone: form.phone,
      });
    }
    emit("success");
  } catch (e: unknown) {
    // Extract error message from response if available
    let errorMessage = "Something went wrong. Please try again or call us on 1300 300 210.";

    if (e && typeof e === "object") {
      const error = e as { data?: { message?: string }; statusMessage?: string };
      if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.statusMessage) {
        errorMessage = error.statusMessage;
      }
    }

    submitError.value = errorMessage;
    console.error("Form submission error:", e);
  } finally {
    isSubmitting.value = false;
  }
}

// Variant-based styling
const containerClass = computed(() => {
  const base = "rounded-2xl";
  switch (props.variant) {
    case "hero":
      return `${base} bg-white p-6 shadow-xl`;
    case "inline":
      return `${base} bg-neutral-50 p-6 border border-neutral-200`;
    case "modal":
      return `${base} bg-white p-8`;
    default:
      return base;
  }
});
</script>

<template>
  <div :class="containerClass">
    <!-- Success State -->
    <div v-if="isSuccess" class="py-8 text-center">
      <div
        class="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-primary-100"
      >
        <Icon name="lucide:check" size="32" class="text-primary-500" />
      </div>
      <h3 class="mb-2 text-xl font-bold text-slate-900">Thank You!</h3>
      <p class="text-neutral-600">
        We've received your enquiry and will be in touch shortly.
      </p>
    </div>

    <!-- Form -->
    <form v-else class="space-y-4" @submit.prevent="handleSubmit">
      <h3 v-if="title" class="mb-4 text-xl font-bold text-slate-900">
        {{ title }}
      </h3>

      <!-- Name -->
      <div class="space-y-1.5">
        <Label for="quick-name"> Name <span class="text-red-500">*</span> </Label>
        <Input
          id="quick-name"
          v-model="form.name"
          type="text"
          :class="{ 'border-red-500 focus-visible:ring-red-500': shouldShowError('name') }"
          @blur="handleBlur('name')"
        />
        <p v-if="shouldShowError('name')" class="text-sm text-red-500">
          {{ errors.name }}
        </p>
      </div>

      <!-- Email -->
      <div class="space-y-1.5">
        <Label for="quick-email"> Email <span class="text-red-500">*</span> </Label>
        <Input
          id="quick-email"
          v-model="form.email"
          type="email"
          :class="{ 'border-red-500 focus-visible:ring-red-500': shouldShowError('email') }"
          @blur="handleBlur('email')"
        />
        <p v-if="shouldShowError('email')" class="text-sm text-red-500">
          {{ errors.email }}
        </p>
      </div>

      <!-- Phone (3/4) + Postcode (1/4) on sm+; stacked on mobile -->
      <div class="grid gap-4 sm:grid-cols-4">
        <div class="space-y-1.5 sm:col-span-3">
          <Label for="quick-phone"> Phone <span class="text-red-500">*</span> </Label>
          <Input
            id="quick-phone"
            v-model="form.phone"
            type="tel"
            :class="{ 'border-red-500 focus-visible:ring-red-500': shouldShowError('phone') }"
            @blur="handleBlur('phone')"
          />
          <p v-if="shouldShowError('phone')" class="text-sm text-red-500">
            {{ errors.phone }}
          </p>
        </div>

        <div class="space-y-1.5 sm:col-span-1">
          <Label for="quick-postcode">Postcode</Label>
          <Input
            id="quick-postcode"
            v-model="form.postcode"
            type="text"
            inputmode="numeric"
            pattern="[0-9]{4}"
            maxlength="4"
            autocomplete="postal-code"
            :aria-invalid="!!errors.postcode"
            :aria-describedby="errors.postcode ? 'quick-postcode-error' : undefined"
            :class="{ 'border-red-500 focus-visible:ring-red-500': errors.postcode }"
          />
          <p v-if="errors.postcode" id="quick-postcode-error" class="text-sm text-red-500">
            {{ errors.postcode }}
          </p>
        </div>
      </div>

      <!-- Company (optional) -->
      <div v-if="showCompany" class="space-y-1.5">
        <Label for="quick-company">Company</Label>
        <Input id="quick-company" v-model="form.company" type="text" />
      </div>

      <!-- Message (optional) -->
      <div class="space-y-1.5">
        <Label for="quick-message">Message</Label>
        <Textarea
          id="quick-message"
          v-model="form.message"
          rows="3"
          class="resize-none"
        />
      </div>

      <!-- Honeypot — hidden from real users, bots will fill it -->
      <div class="absolute -left-[9999px]" aria-hidden="true">
        <input v-model="honeypot" type="text" name="website" tabindex="-1" autocomplete="off" />
      </div>

      <!-- Error Message -->
      <p v-if="submitError" class="text-sm text-red-500">{{ submitError }}</p>

      <!-- Submit Button -->
      <Button
        type="submit"
        :disabled="isSubmitting"
        class="w-full rounded-full bg-primary-400 font-bold hover:bg-primary-500"
        size="lg"
      >
        <span v-if="isSubmitting" class="flex items-center justify-center gap-2">
          <Icon name="lucide:loader-2" size="20" class="animate-spin" />
          Sending...
        </span>
        <span v-else>{{ submitText }}</span>
      </Button>

      <!-- Privacy Note (required for hiding reCAPTCHA badge) -->
      <p class="text-center text-xs text-neutral-500">
        This site is protected by reCAPTCHA. By submitting, you agree to our
        <a
          href="/pdf/terms/Privacy_Policy.pdf"
          target="_blank"
          class="underline hover:text-primary-400"
          >Privacy Policy</a
        >.
      </p>
    </form>
  </div>
</template>
